/**
 * EVENTSFLO-I18N-PHASE-2E-MACHINE-ASSISTED-ADMIN-WORKFLOW-V1
 *
 * SuperAdmin-only Admin i18n translation draft workflow card.
 *
 * Safety:
 *   - Hidden for non-SuperAdmin (returns null).
 *   - Production English UI is unchanged by this card.
 *   - Machine translation is disabled by default; the Generate
 *     button is gated by the backend flag, not the frontend.
 *   - No API key, secret, or provider credential ever appears in
 *     the DOM. The provider name is shown as a label only.
 *   - Approved drafts are stored as drafts-of-record. Production
 *     activation is a later phase.
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Textarea } from "../../../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../components/ui/select";
import {
  AlertTriangle,
  CheckCircle2,
  Info,
  Languages,
  Play,
  RefreshCw,
  Trash2,
  XCircle,
} from "lucide-react";
import { useAuth } from "../../../context/AuthContext";

const ENDPOINT = "/api/admin/i18n";

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return String(iso);
  }
}

async function authedFetch(path, token, options = {}) {
  const r = await fetch(`${process.env.REACT_APP_BACKEND_URL}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
  let body = null;
  try { body = await r.json(); } catch { body = null; }
  if (!r.ok) {
    const detail = body?.detail || body || { error: `HTTP ${r.status}` };
    const msg =
      typeof detail === "string"
        ? detail
        : detail.message || detail.error || `HTTP ${r.status}`;
    const err = new Error(msg);
    err.status = r.status;
    throw err;
  }
  return body;
}

function StatusBadge({ status }) {
  const map = {
    draft: "bg-zinc-500/15 text-zinc-300 border border-zinc-500/30",
    needs_review:
      "bg-amber-500/15 text-amber-300 border border-amber-500/30",
    approved:
      "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30",
    rejected: "bg-rose-500/15 text-rose-300 border border-rose-500/30",
  };
  return (
    <Badge
      className={map[status] || map.draft}
      data-testid={`i18n-draft-status-badge-${status}`}
    >
      {status}
    </Badge>
  );
}

export default function I18nDraftsCard() {
  const { user, token } = useAuth();
  const isSuperAdmin = user?.role === "superadmin";

  const [config, setConfig] = useState(null);
  const [configLoading, setConfigLoading] = useState(false);
  const [configError, setConfigError] = useState(null);

  const [drafts, setDrafts] = useState(null);
  const [draftsLoading, setDraftsLoading] = useState(false);
  const [draftsError, setDraftsError] = useState(null);

  const [filterStatus, setFilterStatus] = useState("all");
  const [filterTargetLanguage, setFilterTargetLanguage] = useState("all");

  // Generate / manual draft form state.
  const [namespace, setNamespace] = useState("admin_backups");
  const [sourceKey, setSourceKey] = useState("");
  const [sourceText, setSourceText] = useState("");
  const [targetLanguage, setTargetLanguage] = useState("fr");
  const [reviewedTranslation, setReviewedTranslation] = useState("");
  const [notes, setNotes] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  const loadConfig = useCallback(async () => {
    if (!isSuperAdmin || !token) return;
    setConfigLoading(true);
    setConfigError(null);
    try {
      const body = await authedFetch(`${ENDPOINT}/config`, token);
      setConfig(body);
      if (body?.target_languages?.length && !body.target_languages.includes(targetLanguage)) {
        setTargetLanguage(body.target_languages[0]);
      }
    } catch (e) {
      setConfigError(e.message || "Failed to load i18n config");
    } finally {
      setConfigLoading(false);
    }
  }, [isSuperAdmin, token, targetLanguage]);

  const loadDrafts = useCallback(async () => {
    if (!isSuperAdmin || !token) return;
    setDraftsLoading(true);
    setDraftsError(null);
    try {
      const params = new URLSearchParams();
      params.set("limit", "25");
      if (filterStatus !== "all") params.set("status", filterStatus);
      if (filterTargetLanguage !== "all")
        params.set("target_language", filterTargetLanguage);
      const body = await authedFetch(
        `${ENDPOINT}/drafts?${params.toString()}`,
        token,
      );
      setDrafts(body);
    } catch (e) {
      setDraftsError(e.message || "Failed to load drafts");
    } finally {
      setDraftsLoading(false);
    }
  }, [isSuperAdmin, token, filterStatus, filterTargetLanguage]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  useEffect(() => {
    loadDrafts();
  }, [loadDrafts]);

  const requiredFieldsOk =
    namespace.trim() && sourceKey.trim() && sourceText.trim() && targetLanguage;

  const createManualDraft = async () => {
    if (!requiredFieldsOk) {
      toast.error("Namespace, source key, source text and target language are required.");
      return;
    }
    setActionLoading(true);
    try {
      await authedFetch(`${ENDPOINT}/drafts`, token, {
        method: "POST",
        body: JSON.stringify({
          namespace: namespace.trim(),
          source_key: sourceKey.trim(),
          source_text: sourceText.trim(),
          target_language: targetLanguage,
          reviewed_translation: reviewedTranslation.trim() || undefined,
          notes: notes.trim() || undefined,
        }),
      });
      toast.success("Manual draft saved");
      setSourceKey("");
      setSourceText("");
      setReviewedTranslation("");
      setNotes("");
      loadDrafts();
    } catch (e) {
      toast.error(e.message || "Could not save draft");
    } finally {
      setActionLoading(false);
    }
  };

  const generateMachineDraft = async () => {
    if (!requiredFieldsOk) {
      toast.error("Namespace, source key, source text and target language are required.");
      return;
    }
    setActionLoading(true);
    try {
      const body = await authedFetch(`${ENDPOINT}/drafts/generate`, token, {
        method: "POST",
        body: JSON.stringify({
          namespace: namespace.trim(),
          source_key: sourceKey.trim(),
          source_text: sourceText.trim(),
          target_language: targetLanguage,
          notes: notes.trim() || undefined,
        }),
      });
      if (body.ok) {
        toast.success("Machine translation draft generated (needs review)");
        loadDrafts();
      } else {
        toast.warning(
          `Machine translation not run — ${body.reason || "unknown"}`,
        );
      }
    } catch (e) {
      toast.error(e.message || "Generate failed");
    } finally {
      setActionLoading(false);
    }
  };

  const approveDraft = async (id) => {
    setActionLoading(true);
    try {
      await authedFetch(`${ENDPOINT}/drafts/${id}/approve`, token, {
        method: "POST",
        body: JSON.stringify({}),
      });
      toast.success("Draft approved (not live in production)");
      loadDrafts();
    } catch (e) {
      toast.error(e.message || "Approve failed");
    } finally {
      setActionLoading(false);
    }
  };

  const rejectDraft = async (id) => {
    setActionLoading(true);
    try {
      await authedFetch(`${ENDPOINT}/drafts/${id}/reject`, token, {
        method: "POST",
        body: JSON.stringify({}),
      });
      toast.success("Draft rejected");
      loadDrafts();
    } catch (e) {
      toast.error(e.message || "Reject failed");
    } finally {
      setActionLoading(false);
    }
  };

  const deleteDraft = async (id) => {
    setActionLoading(true);
    try {
      await authedFetch(`${ENDPOINT}/drafts/${id}`, token, { method: "DELETE" });
      toast.success("Draft deleted");
      loadDrafts();
    } catch (e) {
      toast.error(e.message || "Delete failed");
    } finally {
      setActionLoading(false);
    }
  };

  if (!isSuperAdmin) return null;

  const machineEnabled = !!config?.machine_translation_enabled;
  const provider = config?.provider || "none";
  const targetLanguages = config?.target_languages || ["fr", "it", "es"];
  const machineUsable = machineEnabled && provider === "mock";

  return (
    <Card data-testid="i18n-drafts-card">
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          <Languages className="w-4 h-4" /> Admin i18n translation drafts
        </CardTitle>
        <p className="text-xs text-zinc-400 mt-1">
          SuperAdmin-only. Machine translations are drafts. They are
          not used in production until approved and explicitly
          enabled in a later phase. English remains the default.
        </p>
      </CardHeader>
      <CardContent>
        {configError ? (
          <div
            className="rounded-md border border-rose-500/30 bg-rose-500/5 p-3 text-xs text-rose-300 flex items-start gap-2"
            data-testid="i18n-config-error"
          >
            <XCircle className="w-4 h-4 mt-0.5" />
            <span>Could not load i18n config: {configError}</span>
          </div>
        ) : configLoading || !config ? (
          <div className="text-sm text-zinc-400" data-testid="i18n-config-loading">
            Loading i18n config…
          </div>
        ) : (
          <div
            className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm"
            data-testid="i18n-config-grid"
          >
            <div>
              <div className="text-zinc-400 text-xs">Machine translation</div>
              <div className="mt-0.5">
                <Badge
                  className={
                    machineEnabled
                      ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                      : "bg-zinc-500/15 text-zinc-300 border border-zinc-500/30"
                  }
                  data-testid="i18n-machine-enabled-badge"
                >
                  {machineEnabled ? "Enabled" : "Disabled"}
                </Badge>
              </div>
            </div>
            <div>
              <div className="text-zinc-400 text-xs">Provider</div>
              <div className="text-zinc-300 mt-0.5" data-testid="i18n-provider">
                {provider}
              </div>
            </div>
            <div>
              <div className="text-zinc-400 text-xs">Source language</div>
              <div className="text-zinc-300 mt-0.5">
                {config.source_language}
              </div>
            </div>
            <div>
              <div className="text-zinc-400 text-xs">Target languages</div>
              <div
                className="text-zinc-300 mt-0.5 font-mono text-xs"
                data-testid="i18n-target-languages"
              >
                {targetLanguages.join(", ")}
              </div>
            </div>
          </div>
        )}

        <div
          className="mt-3 text-xs text-zinc-500 flex items-start gap-2"
          data-testid="i18n-production-unchanged-note"
        >
          <Info className="w-3.5 h-3.5 mt-0.5" />
          <span>
            Production language is unchanged. Approved drafts are
            <strong className="text-zinc-300"> not </strong>
            wired into the runtime translation lookup yet.
          </span>
        </div>

        {/* Draft form */}
        <div
          className="mt-4 rounded-md border border-zinc-700/40 p-3"
          data-testid="i18n-draft-form-section"
        >
          <div className="text-sm font-medium text-zinc-300 mb-2 flex items-center gap-2">
            <Play className="w-3.5 h-3.5" /> Create or generate a draft
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label htmlFor="i18n-namespace" className="text-xs text-zinc-400">
                namespace
              </Label>
              <Input
                id="i18n-namespace"
                value={namespace}
                onChange={(e) => setNamespace(e.target.value)}
                placeholder="admin_backups"
                data-testid="i18n-namespace-input"
              />
            </div>
            <div>
              <Label htmlFor="i18n-source-key" className="text-xs text-zinc-400">
                source key
              </Label>
              <Input
                id="i18n-source-key"
                value={sourceKey}
                onChange={(e) => setSourceKey(e.target.value)}
                placeholder="TEST_i18n_admin_backup_title"
                data-testid="i18n-source-key-input"
              />
            </div>
            <div>
              <Label className="text-xs text-zinc-400">target language</Label>
              <Select
                value={targetLanguage}
                onValueChange={setTargetLanguage}
              >
                <SelectTrigger data-testid="i18n-target-language-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {targetLanguages.map((l) => (
                    <SelectItem
                      key={l}
                      value={l}
                      data-testid={`i18n-target-language-option-${l}`}
                    >
                      {l}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label htmlFor="i18n-source-text" className="text-xs text-zinc-400">
                source text (English)
              </Label>
              <Textarea
                id="i18n-source-text"
                value={sourceText}
                onChange={(e) => setSourceText(e.target.value)}
                placeholder="Admin Backups"
                rows={3}
                data-testid="i18n-source-text-input"
              />
            </div>
            <div>
              <Label
                htmlFor="i18n-reviewed-translation"
                className="text-xs text-zinc-400"
              >
                reviewed translation (optional, manual)
              </Label>
              <Textarea
                id="i18n-reviewed-translation"
                value={reviewedTranslation}
                onChange={(e) => setReviewedTranslation(e.target.value)}
                placeholder="Sauvegardes administrateur"
                rows={3}
                data-testid="i18n-reviewed-translation-input"
              />
            </div>
          </div>

          <div className="mt-3">
            <Label htmlFor="i18n-notes" className="text-xs text-zinc-400">
              notes (optional)
            </Label>
            <Input
              id="i18n-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Reviewer notes"
              data-testid="i18n-notes-input"
            />
          </div>

          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              onClick={createManualDraft}
              disabled={!requiredFieldsOk || actionLoading}
              data-testid="i18n-create-manual-draft-btn"
            >
              <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Save manual draft
            </Button>
            <Button
              variant="outline"
              onClick={generateMachineDraft}
              disabled={!requiredFieldsOk || actionLoading || !machineUsable}
              data-testid="i18n-generate-machine-draft-btn"
            >
              <Play className="w-3.5 h-3.5 mr-1" /> Generate machine draft
            </Button>
            {!machineUsable ? (
              <div
                className="text-xs text-zinc-400 flex items-center gap-1"
                data-testid="i18n-machine-disabled-helper"
              >
                <AlertTriangle className="w-3.5 h-3.5 text-amber-400" />
                Machine translation is disabled or provider is not
                configured. Manual drafts only.
              </div>
            ) : null}
          </div>
        </div>

        {/* Filters + list */}
        <div
          className="mt-4 rounded-md border border-zinc-700/40 p-3"
          data-testid="i18n-drafts-list-section"
        >
          <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
            <div className="text-sm font-medium text-zinc-300 flex items-center gap-2">
              <Info className="w-3.5 h-3.5" /> Drafts
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger
                  className="w-32"
                  data-testid="i18n-filter-status-select"
                >
                  <SelectValue placeholder="status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">all</SelectItem>
                  <SelectItem value="draft">draft</SelectItem>
                  <SelectItem value="needs_review">needs_review</SelectItem>
                  <SelectItem value="approved">approved</SelectItem>
                  <SelectItem value="rejected">rejected</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={filterTargetLanguage}
                onValueChange={setFilterTargetLanguage}
              >
                <SelectTrigger
                  className="w-28"
                  data-testid="i18n-filter-target-language-select"
                >
                  <SelectValue placeholder="lang" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">all</SelectItem>
                  {targetLanguages.map((l) => (
                    <SelectItem key={l} value={l}>
                      {l}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button
                size="sm"
                variant="outline"
                onClick={loadDrafts}
                disabled={draftsLoading}
                data-testid="i18n-drafts-refresh-btn"
              >
                <RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh
              </Button>
            </div>
          </div>

          {draftsError ? (
            <div
              className="rounded-md border border-rose-500/30 bg-rose-500/5 p-2 text-xs text-rose-300"
              data-testid="i18n-drafts-error"
            >
              {draftsError}
            </div>
          ) : !drafts ? (
            <div className="text-xs text-zinc-500">Loading drafts…</div>
          ) : drafts.rows?.length === 0 ? (
            <div
              className="text-xs text-zinc-500"
              data-testid="i18n-drafts-empty"
            >
              No drafts yet. Use the form above to create or
              generate one.
            </div>
          ) : (
            <div
              className="overflow-auto rounded border border-zinc-700/40"
              data-testid="i18n-drafts-table"
            >
              <table className="w-full text-xs">
                <thead className="bg-zinc-800/60 text-zinc-400">
                  <tr>
                    <th className="text-left px-2 py-1">namespace · key</th>
                    <th className="text-left px-2 py-1">target</th>
                    <th className="text-left px-2 py-1">source text</th>
                    <th className="text-left px-2 py-1">draft</th>
                    <th className="text-left px-2 py-1">status</th>
                    <th className="text-left px-2 py-1">updated</th>
                    <th className="text-right px-2 py-1">actions</th>
                  </tr>
                </thead>
                <tbody>
                  {drafts.rows.map((r) => (
                    <tr
                      key={r.id}
                      className="odd:bg-zinc-900/30 align-top"
                      data-testid={`i18n-draft-row-${r.id}`}
                    >
                      <td className="px-2 py-1 text-zinc-300">
                        <div className="font-mono text-xs">{r.namespace}</div>
                        <div className="font-mono text-xs text-zinc-500">
                          {r.source_key}
                        </div>
                      </td>
                      <td className="px-2 py-1 text-zinc-300 font-mono">
                        {r.target_language}
                      </td>
                      <td
                        className="px-2 py-1 text-zinc-300 max-w-xs whitespace-pre-wrap"
                        data-testid={`i18n-draft-source-${r.id}`}
                      >
                        {r.source_text}
                      </td>
                      <td className="px-2 py-1 text-zinc-300 max-w-xs whitespace-pre-wrap">
                        {r.reviewed_translation ? (
                          <div data-testid={`i18n-draft-reviewed-${r.id}`}>
                            <div className="text-emerald-300 text-xs">reviewed</div>
                            {r.reviewed_translation}
                          </div>
                        ) : r.machine_translation ? (
                          <div data-testid={`i18n-draft-machine-${r.id}`}>
                            <div className="text-amber-300 text-xs">machine</div>
                            {r.machine_translation}
                          </div>
                        ) : (
                          <span className="text-zinc-500">—</span>
                        )}
                      </td>
                      <td className="px-2 py-1">
                        <StatusBadge status={r.status} />
                      </td>
                      <td className="px-2 py-1 text-zinc-400">
                        {fmt(r.updated_at)}
                      </td>
                      <td className="px-2 py-1 text-right">
                        <div className="inline-flex gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => approveDraft(r.id)}
                            disabled={
                              actionLoading || r.status === "approved"
                            }
                            data-testid={`i18n-draft-approve-btn-${r.id}`}
                          >
                            <CheckCircle2 className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => rejectDraft(r.id)}
                            disabled={
                              actionLoading || r.status === "rejected"
                            }
                            data-testid={`i18n-draft-reject-btn-${r.id}`}
                          >
                            <XCircle className="w-3 h-3" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => deleteDraft(r.id)}
                            disabled={actionLoading}
                            data-testid={`i18n-draft-delete-btn-${r.id}`}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {drafts ? (
            <div
              className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 text-xs"
              data-testid="i18n-drafts-aggregates"
            >
              <div>
                <div className="text-zinc-400">total</div>
                <div className="text-zinc-300" data-testid="i18n-drafts-total">
                  {drafts.total}
                </div>
              </div>
              <div>
                <div className="text-zinc-400">shown</div>
                <div className="text-zinc-300">{drafts.returned}</div>
              </div>
              <div>
                <div className="text-zinc-400">filter status</div>
                <div className="text-zinc-300">
                  {drafts.filters?.status || "all"}
                </div>
              </div>
              <div>
                <div className="text-zinc-400">filter target</div>
                <div className="text-zinc-300">
                  {drafts.filters?.target_language || "all"}
                </div>
              </div>
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}
